import { Alert } from 'antd'
import { observer } from 'mobx-react-lite'
import { useEffect } from 'react'
import { MdOutlineLightbulb, MdOutlineWarningAmber } from 'react-icons/md'

import { tutorialManager } from '@/Ship/Stores/TutorialManager'

import type { ReactNode } from 'react'

export interface HintProps {
    /**
     * Стабільний ідентифікатор підказки.
     *
     * За ним запам'ятовується «більше не показувати», тож він мусить пережити
     * переписування тексту: змінили формулювання — підказка **не** має
     * повернутися до того, хто її вже прибрав. Формат — `розділ.місце`.
     */
    id: string

    /** Коротка назва правила. Без неї підказка — один рядок тексту. */
    title?: string

    /** Пояснення. */
    children: ReactNode

    /**
     * Тон. `info` — типовий; `warning` — коли пояснення про наслідок, який не
     * відкотити (списання зі складу, незворотний перехід статусу).
     */
    tone?: 'info' | 'warning'
}

/**
 * Пояснення для того, хто працює з системою вперше.
 *
 * Єдине місце, де живе поведінка підказок: увімкнення й вимкнення масово
 * («Профіль → Інтерфейс») і поштучно (хрестик). Обгортка потрібна саме заради
 * цього — пояснення розкидані по двох десятках екранів, і без спільного
 * компонента «вимкнути їх усі» означало б правку двох десятків файлів.
 *
 * ⚠️ Підказка — **не** повідомлення про стан. Помилка сервера, «документ
 * проведено, редагування закрите», порожній список — усе це має лишатися
 * звичайним `Alert`: сховати їх означало б сховати те, без чого екран не
 * зрозуміти взагалі, а не лише новачкові.
 *
 * ⚠️ Запит на стан робить сама підказка (`load()` дедуплікований), тож
 * вставити її можна будь-де, нічого не під'єднуючи в шаблоні сторінки.
 *
 * ⚠️ Кнопка закриття підписана словами, а не хрестиком. Хрестик спершу й
 * стояв — із підказкою на наведення, яка **не з'являлася** (antd загортає
 * `closeIcon` у власну кнопку, і `Tooltip` усередині неї не спрацьовує).
 * Виходив безіменний хрестик на блоці, який зникає назавжди: замовник спитав
 * «що це за хрестики і як воно працює» — і це була справедлива претензія.
 * Слова коштують сотні пікселів праворуч і знімають питання цілком.
 */
export const Hint = observer(({ id, title, children, tone = 'info' }: HintProps) => {
    useEffect(() => {
        void tutorialManager.load()
    }, [])

    if (!tutorialManager.shows(id)) return null

    const icon = tone === 'warning' ? <MdOutlineWarningAmber /> : <MdOutlineLightbulb />

    return (
        <Alert
            type={tone}
            showIcon
            icon={icon}
            {...(title ? { message: title, description: children } : { message: children })}
            closable
            closeIcon={<span style={{ fontSize: 12, whiteSpace: 'nowrap' }}>Не показувати</span>}
            onClose={() => void tutorialManager.dismiss(id)}
            style={{ marginBottom: 16 }}
        />
    )
})
